"use client";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { FilterButton } from "@components/commerce/product-listing/filters/filter-button/FilterButton";
import { ToolbarFilterItem } from "@components/commerce/product-listing/filters/toolbar/item/ToolbarFilterItem";
import SortBy from "@components/commerce/product-listing/filters/toolbar/item/sortBy/SortBy";
import type { IPartFilter } from "@contentful-api/types/contentful";
import { CommonCMS } from "@lib/constants/contentful";
import { TARGET_GROUP_FACET } from "@lib/constants/filtersConstants";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Flex } from "@ecco/ui/flex";
import { Grid } from "@ecco/ui/grid";
import { Box } from "@ecco/ui/box";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { Collapse } from "@ecco/ui/collapse";
import { useOutsideClick } from "@ecco/ui/use-outside-click";
import dynamic from "next/dynamic";
import { memo, useEffect, useRef } from "react";
import {
    filterButtonStyles,
    filtersContainerStyles,
    filtersDialogCollapse,
    overlayStyle,
    resultsStyles,
    wrapperStyles,
} from "./FiltersToolbarStyles";
import { KeepFiltersDialogInView } from "./KeepFiltersDialogInView";
import { Overlay } from "./overlay/Overlay";
import { useFilterStore } from "@store/filterStore";
import { useStore } from "@store/index";

const SelectedFilters = dynamic(
    () => import("@components/commerce/product-listing/filters/toolbar/selected/SelectedFilters"),
    { ssr: false }
);

const FiltersDialog = dynamic(
    () => import("@components/commerce/product-listing/filters/dialog/FiltersDialog"),
    { ssr: false }
);

export const FILTER_DIALOG_TOGGLE_BUTTON_ID = "filter-dialog-toggle-button";

interface FiltersToolbarProps {
    listOfFilters: IPartFilter[];
    locale: string;
}

const filtersExcludingTargetGroup = (listOfFilters: IPartFilter[]) =>
    listOfFilters.filter((filter) => filter?.fields?.facetName !== TARGET_GROUP_FACET);

export const FiltersToolbar = memo(({ listOfFilters, locale }: FiltersToolbarProps) => {
    const toolbarFilters = filtersExcludingTargetGroup(listOfFilters);
    // Dynamic values from store (selective subscriptions)
    const filtersDialogOpened = useFilterStore((state) => state.filtersDialogOpened);
    const mobileSortByOpen = useFilterStore((state) => state.mobileSortByOpen);
    const setFiltersDialogOpened = useFilterStore((state) => state.setFiltersDialogOpened);
    const closeFiltersDialog = useFilterStore((state) => state.closeFiltersDialog);
    const hasActiveTimer = useStore((state) => state.data.hasActiveTimer);

    const filterRef = useRef<HTMLDivElement>(null);
    const filterDialogRef = useRef<HTMLDivElement>(null);
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const { get: getMicrocopy } = useMicrocopy();
    const { modalType } = useGlobalModalContext();

    const FilterButtonIcon = filtersDialogOpened ? (
        <ECCOIcon aria-hidden name="close" size="sm" />
    ) : (
        <ECCOIcon aria-hidden name="filter" size="sm" />
    );

    useOutsideClick({
        enabled: filtersDialogOpened,
        ref: filterDialogRef,
        handler: (event) => {
            if ((event.target as HTMLElement).id === FILTER_DIALOG_TOGGLE_BUTTON_ID) return;
            if (event.target !== filterDialogRef.current) {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                !modalType && filtersDialogOpened && closeFiltersDialog();
                filterButtonRef.current.blur();
            }
        },
    });

    const handleFilterButtonClick = () => {
        setFiltersDialogOpened();
        if (filtersDialogOpened) return;
        filterButtonRef.current?.scrollIntoView();
    };

    useEffect(() => {
        if (filtersDialogOpened && filterDialogRef.current) {
            filterDialogRef.current.focus();
        }
    }, [filtersDialogOpened]);

    return (
        <Flex sx={{ ...wrapperStyles, pt: hasActiveTimer ? 8 : 2 }} ref={filterRef}>
            {filtersDialogOpened && <KeepFiltersDialogInView toolbarRef={filterRef} />}
            <Grid sx={filtersContainerStyles}>
                <Flex>
                    <Flex data-testid="product-filters" sx={filterButtonStyles}>
                        {toolbarFilters.map((filter, index) => (
                            <ToolbarFilterItem key={filter?.sys?.id || index} {...filter?.fields} />
                        ))}
                    </Flex>
                    <Collapse
                        ref={filterDialogRef}
                        style={filtersDialogCollapse}
                        in={filtersDialogOpened}
                        tabIndex={-1}
                    >
                        <FiltersDialog listOfFilters={listOfFilters} />
                    </Collapse>
                    <Collapse in={filtersDialogOpened || mobileSortByOpen} animateOpacity>
                        <Overlay sx={overlayStyle} />
                    </Collapse>
                </Flex>
                <Flex sx={resultsStyles}>
                    <FilterButton
                        ref={filterButtonRef}
                        onClick={handleFilterButtonClick}
                        id={FILTER_DIALOG_TOGGLE_BUTTON_ID}
                        icon={FilterButtonIcon}
                        text={getMicrocopy(CommonCMS.page, CommonCMS.button.allFilters)}
                        iconPlacement="right"
                        type="top"
                    />
                    {
                        <Box sx={{ visibility: ["visible", "visible", "visible", "hidden"] }}>
                            <SortBy locale={locale} />
                        </Box>
                    }
                </Flex>
            </Grid>
            <SelectedFilters locale={locale} />
        </Flex>
    );
});

FiltersToolbar.displayName = "FiltersToolbar";
